import { useConfig } from "@/core/config/ConfigContext";
import { useStoreGroupMembersQuery } from "../api/useStoreGroupsApi";
import { useFetchGroupStoresInfiniteQuery } from "./useFetchGroupStoresInfiniteQuery";
import { UseDataTableOptions } from "@/types/dataTableSettings";
import { ReadStoreGroupMember } from "@/types/storeGroupMember";
import { QueryKey } from "@tanstack/react-query";
import { ListOptions } from "../api/StoreGroupService";
import appConfiguration from "@/components/cfg/AppCfg";

function isDataTableOptions(
  value: UseDataTableOptions<ReadStoreGroupMember> | ListOptions | undefined,
): value is UseDataTableOptions<ReadStoreGroupMember> {
  return Array.isArray(value?.sorting);
}

function mapDataTableFiltersToListFilters(
  filters?: UseDataTableOptions<ReadStoreGroupMember>["filters"],
): NonNullable<ListOptions["filters"]> {
  if (!filters) {
    return {};
  }

  if (!Array.isArray(filters)) {
    return filters as unknown as NonNullable<ListOptions["filters"]>;
  }

  const mapped: NonNullable<ListOptions["filters"]> = {};

  for (const filterEntry of filters) {
    const filterType = filterEntry.value.filter;
    if (!filterType) continue;

    if (!(filterEntry.id in mapped)) {
      mapped[filterEntry.id] = {};
    }

    const existing = mapped[filterEntry.id] as Record<string, unknown>;
    existing[filterType] = filterEntry.value.value;
  }

  return mapped;
}

/**
 * Wrapper hook for fetching store group members (stores within a group).
 * Encapsulates feature flag logic - components never see it.
 *
 * - FF enabled: Uses new StoreGroupService via useStoreGroupMembersQuery (Phase 2)
 * - FF disabled: Uses legacy hook useFetchGroupStoresInfiniteQuery
 *
 * Both return compatible infinite query structures for table pagination.
 *
 * @example
 * const { data, isLoading, hasNextPage } = useStoreGroupMembers(groupId, options, queryKey);
 */
export function useStoreGroupMembers(
  groupId: string,
  options?: UseDataTableOptions<ReadStoreGroupMember> | ListOptions,
  queryKey?: QueryKey,
) {
  const config = useConfig();
  const useNewService = config.isFeatureEnabled("newStoreGroupServiceEnabled");

  // Ensure queryKey is a proper array format
  const finalQueryKey = queryKey || [`storeGroupStores`, groupId];

  // Convert table options to service options when needed.
  const listOptionsBase: ListOptions | undefined = isDataTableOptions(options)
    ? {
        filters: mapDataTableFiltersToListFilters(options.filters),
        sorting: (() => {
          const firstSort = options.sorting?.[0];
          if (!firstSort) return undefined;
          return {
            [firstSort.id]: firstSort.desc ? "desc" : "asc",
          };
        })(),
      }
    : options;

  // Keep default limit resolution at hook/adapter boundary, not in domain service.
  const listOptions: ListOptions = {
    ...(listOptionsBase ?? {}),
    limit: listOptionsBase?.limit ?? appConfiguration.paginationLimit,
  };

  const dataTableOptions = isDataTableOptions(options)
    ? options
    : (options as UseDataTableOptions<ReadStoreGroupMember> | undefined) ??
      ({} as UseDataTableOptions<ReadStoreGroupMember>);

  const newServiceQuery = useStoreGroupMembersQuery(
    useNewService ? groupId : "",
    listOptions,
  );

  const legacyQuery = useFetchGroupStoresInfiniteQuery(
    useNewService ? undefined : groupId,
    dataTableOptions,
    finalQueryKey,
  );

  return useNewService ? newServiceQuery : legacyQuery;
}
